import { action, computed } from 'mobx';
import {
    ApproveLoanApplicationRequest,
    LoanApplicationAgentAPIApiFindAllLoanApplicationsForAgentRequest,
    LoanApplicationDto,
} from 'src/generated-api-client';
import { agentLoanApplicationsApi } from 'src/services/apiServices';
import { AsyncOperationWithStatus } from 'src/utils/mobx/AsyncOperationWithStatus';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { BasicStoreApi } from 'src/utils/mobx/BasicStore/BasicStore.types';
import { FilterCriteria } from 'src/utils/mobx/FilterCriteria';
import { Pager } from 'src/utils/mobx/Pager';
import { Sorter } from 'src/utils/mobx/Sorter';
import { RequestHelper } from 'src/utils/RequestHelper';

export class AgentLoanApplicationsStoreClass extends BasicStore<
    LoanApplicationDto,
    Record<string, any>,
    LoanApplicationDto,
    LoanApplicationDto,
    LoanApplicationDto
> {
    pager = new Pager();
    sorter = new Sorter<LoanApplicationDto>();
    filterCriteria =
        new FilterCriteria<LoanApplicationAgentAPIApiFindAllLoanApplicationsForAgentRequest>();

    api: BasicStoreApi<
        LoanApplicationDto,
        LoanApplicationDto,
        LoanApplicationDto,
        LoanApplicationDto
    > = {
        loadList: async (...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            const response = await RequestHelper.unwrapFromAxiosPromise(
                agentLoanApplicationsApi.findAllLoanApplicationsForAgent(
                    {
                        ...this.filterCriteria.filter,
                        page: this.pager?.page,
                        size: this.pager?.size,
                        sort: this.sorter?.sort,
                    },
                    options,
                ),
            );
            this.pager?.setTotal(response.totalElements ?? 0);

            return response.content ?? [];
        },
        loadItem: async (id, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            const response = await RequestHelper.unwrapFromAxiosPromise(
                agentLoanApplicationsApi.getLoanApplicationForAgent(
                    { id },
                    options,
                ),
            );

            return response ?? null;
        },
    };

    merchantSnapshotsLoader = new AsyncOperationWithStatus((id: string) => {
        return RequestHelper.unwrapFromAxiosPromise(
            agentLoanApplicationsApi.getMerchantSnapshotsForAgent({ id }),
        );
    });

    declineLoader = new AsyncOperationWithStatus(
        (applicationId: string, reason: string) => {
            return RequestHelper.unwrapFromAxiosPromise(
                agentLoanApplicationsApi.declineLoanApplicationForAgent({
                    id: applicationId,
                    declineLoanApplicationRequest: {
                        reason,
                    },
                }),
            );
        },
    );

    approveLoader = new AsyncOperationWithStatus(
        (applicationId: string, data: ApproveLoanApplicationRequest) => {
            return RequestHelper.unwrapFromAxiosPromise(
                agentLoanApplicationsApi.approveLoanApplicationForAgent({
                    id: applicationId,
                    approveLoanApplicationRequest: data,
                }),
            );
        },
    );

    transactionsLoader = new AsyncOperationWithStatus((id: string) => {
        return RequestHelper.unwrapFromAxiosPromise(
            agentLoanApplicationsApi.getTransactionsForAgent({ id }),
        );
    });

    @action async loadCheckPenaltyData(id: string) {
        await this.merchantSnapshotsLoader.call(id);
    }

    @action
    decline = (applicationId: string, reason: string) => {
        return this.declineLoader.call(applicationId, reason);
    };

    @action
    approve = (applicationId: string, data: ApproveLoanApplicationRequest) => {
        return this.approveLoader.call(applicationId, data);
    };

    @action
    loadTransactions(id: string) {
        return this.transactionsLoader.call(id);
    }

    @computed get merchantSnapshots() {
        return this.merchantSnapshotsLoader.data;
    }

    @computed get transactions() {
        return this.transactionsLoader.data || [];
    }
}

export const AgentLoanApplicationsStore = new AgentLoanApplicationsStoreClass();
